/**
 * Job Import Wizard
 *
 * Multi-step wizard for importing jobs from documents (PDF/DOC/DOCX) or URLs.
 * Uses AI to extract structured job fields, then hands off to JobForm for review.
 *
 * Feature: #5182 - Import Job from Document or URL
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stepper, Step, StepLabel, Paper } from "@mui/material";
import SourceSelection from "./Step1_SourceSelection";
import InputStep from "./Step2_Input";
import ParseStep from "./Step3_Parse";
import JobForm from "../JobForm";

export type ImportMethod = "document" | "url";

export type EmploymentType =
  | "Full-time"
  | "Part-time"
  | "Contract"
  | "Freelance"
  | "Remote";

export type SeniorityLevel = "Junior" | "Mid" | "Senior" | "Lead" | "Principal";

export interface ParsedJobData {
  title: string | null;
  department: string | null;
  location: string | null;
  employment_type: EmploymentType | null;
  seniority: SeniorityLevel | null;
  skills: string[];
  description: string | null;
  tags: string[];
}

export interface JobImportWizardState {
  importMethod: ImportMethod;
  // Document mode
  uploadedFile?: File;
  // URL mode
  url?: string;
  // Parse results
  parsedData?: ParsedJobData | null;
  parseError?: string | null;
  rawText?: string;
  // Source tracking
  importSourceRef?: string;
}

const STEPS = ["Source", "Input", "Parse", "Review & Create"];

const JobImportWizard: React.FC = () => {
  const navigate = useNavigate();

  type WizardStep = 1 | 2 | 3 | 4;
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [wizardState, setWizardState] = useState<JobImportWizardState>({
    importMethod: "document",
  });

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 4) as WizardStep);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as WizardStep);
  };

  const updateWizardState = (updates: Partial<JobImportWizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }));
  };

  const handleJobSaved = () => {
    navigate("/jobs");
  };

  const handleCancel = () => {
    navigate("/jobs");
  };

  // Step 4 renders JobForm directly (no Paper wrapper)
  if (currentStep === 4) {
    const initialData = wizardState.parsedData
      ? {
          title: wizardState.parsedData.title || undefined,
          department: wizardState.parsedData.department || undefined,
          location: wizardState.parsedData.location || undefined,
          employment_type: wizardState.parsedData.employment_type || undefined,
          seniority: wizardState.parsedData.seniority || undefined,
          skills: wizardState.parsedData.skills,
          description: wizardState.parsedData.description || undefined,
          tags: wizardState.parsedData.tags,
          import_source_type: wizardState.importMethod,
          import_source_ref: wizardState.importSourceRef,
        }
      : undefined;

    return (
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
        <Stepper activeStep={3} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <JobForm
          job={initialData}
          onSave={handleJobSaved}
          onCancel={handleCancel}
          variant="inline"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Stepper activeStep={currentStep - 1} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3, minHeight: 400 }}>
        {currentStep === 1 && (
          <SourceSelection
            onNext={handleNext}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 2 && (
          <InputStep
            wizardState={wizardState}
            onNext={handleNext}
            onBack={handleBack}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 3 && (
          <ParseStep
            wizardState={wizardState}
            onNext={handleNext}
            onBack={handleBack}
            updateState={updateWizardState}
          />
        )}
      </Paper>
    </Box>
  );
};

export default JobImportWizard;
